import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import { taskTrackerApi } from '../../api/taskTracker.api'
import type { TaskTrackerCrmLeadTreeItem } from '../taskTrackerCrmLeadSticker.types'

const TREE_PAGE_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 600

type UseCrmLeadStickerTreeSearchParams = {
  enabled: boolean
}

export const useCrmLeadStickerTreeSearch = ({
  enabled,
}: UseCrmLeadStickerTreeSearchParams) => {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [items, setItems] = useState<TaskTrackerCrmLeadTreeItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestIdRef = useRef(0)
  const nextCursorRef = useRef<string | null>(null)
  const loadingMoreRef = useRef(false)

  nextCursorRef.current = nextCursor
  loadingMoreRef.current = loadingMore

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current += 1
      setSearchInput('')
      setDebouncedSearch('')
      setItems([])
      setNextCursor(null)
      setLoading(false)
      setLoadingMore(false)
      setError(null)
    }
  }, [enabled])

  useDebounce(
    () => {
      if (!enabled) return

      const trimmedSearch = searchInput.trim()
      if (trimmedSearch === debouncedSearch) return

      setDebouncedSearch(trimmedSearch)
    },
    SEARCH_DEBOUNCE_MS,
    [debouncedSearch, enabled, searchInput],
  )

  useEffect(() => {
    if (!enabled) return

    const requestId = ++requestIdRef.current
    let cancelled = false

    const loadFirstPage = async () => {
      setLoading(true)
      setError(null)
      setItems([])
      setNextCursor(null)

      try {
        const page = await taskTrackerApi.getCrmLeadTree({
          search: debouncedSearch,
          cursor: 0,
          limit: TREE_PAGE_LIMIT,
        })

        if (cancelled || requestId !== requestIdRef.current) return

        setItems(page.items)
        setNextCursor(page.nextCursor)
      } catch (err: unknown) {
        if (cancelled || requestId !== requestIdRef.current) return
        setItems([])
        setNextCursor(null)
        setError(
          err instanceof Error ? err.message : 'Не удалось загрузить список лидов',
        )
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    }

    void loadFirstPage()

    return () => {
      cancelled = true
    }
  }, [debouncedSearch, enabled])

  const loadMore = useCallback(async () => {
    const cursor = nextCursorRef.current
    if (!enabled || !cursor || loadingMoreRef.current) return

    const requestId = requestIdRef.current
    setLoadingMore(true)

    try {
      const page = await taskTrackerApi.getCrmLeadTree({
        search: debouncedSearch,
        cursor,
        limit: TREE_PAGE_LIMIT,
      })

      if (requestId !== requestIdRef.current) return

      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.leadId))
        const appended = page.items.filter((item) => !seen.has(item.leadId))
        return appended.length ? [...prev, ...appended] : prev
      })
      setNextCursor(page.nextCursor)
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return
      setError(
        err instanceof Error ? err.message : 'Не удалось догрузить список лидов',
      )
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingMore(false)
      }
    }
  }, [debouncedSearch, enabled])

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
  }, [])

  const reset = useCallback(() => {
    setSearchInput('')
    setDebouncedSearch('')
    setItems([])
    setNextCursor(null)
    setError(null)
    setLoading(false)
    setLoadingMore(false)
  }, [])

  return {
    searchInput,
    setSearchInput: handleSearchChange,
    items,
    hasMore: nextCursor != null,
    loading,
    loadingMore,
    error,
    loadMore,
    reset,
  }
}
